/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Badge, Flex, Table, Text, VisuallyHidden } from '@radix-ui/themes';

import { SortableTableHeader, type SortDirection } from '../../../../shared/SortableTableHeader';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';
import type { EvaluationRow, EvaluationSortField } from './evaluations.types';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const SORTABLE_COLUMNS: { sortKey: EvaluationSortField; label: string }[] = [
  { sortKey: 'PUBLIC_ID', label: STRINGS.columns.repository },
  { sortKey: 'FORMAT', label: STRINGS.columns.format },
];

const COUNT_COLUMNS: { sortKey: EvaluationSortField; label: string }[] = [
  { sortKey: 'COMPONENTS_EVALUATED', label: STRINGS.columns.componentsEvaluated },
  { sortKey: 'CRITICAL_VIOLATION_COUNT', label: STRINGS.columns.critical },
  { sortKey: 'SEVERE_VIOLATION_COUNT', label: STRINGS.columns.severe },
  { sortKey: 'MODERATE_VIOLATION_COUNT', label: STRINGS.columns.moderate },
];

/** The browser's local time, or the "never" text when IQ has no evaluation or sent something unreadable. */
function formatEvaluationTime(value: string | null): string {
  if (!value) return STRINGS.values.neverEvaluated;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? STRINGS.values.neverEvaluated : TIME_FORMAT.format(date);
}

interface ThreatCountBadgeProps {
  count: number;
  color: 'red' | 'orange' | 'yellow';
  label: string;
}

function ThreatCountBadge({ count, color, label }: ThreatCountBadgeProps): JSX.Element {
  return (
    <Badge
      size="1"
      radius="full"
      color={count > 0 ? color : 'gray'}
      variant={count > 0 ? 'solid' : 'soft'}
      title={STRINGS.countTitle(count, label)}
      style={{ minWidth: 35, justifyContent: 'center' }}
    >
      {count.toLocaleString()}
    </Badge>
  );
}

export interface EvaluationsTableProps {
  rows: EvaluationRow[];
  sortBy: EvaluationSortField;
  asc: boolean;
  onSort: (sortBy: EvaluationSortField, asc: boolean) => void;
}

export function EvaluationsTable({ rows, sortBy, asc, onSort }: EvaluationsTableProps): JSX.Element {
  const handleSort = (key: string, direction: SortDirection) => onSort(key as EvaluationSortField, direction === 'asc');
  const sortProps = {
    currentSortKey: sortBy,
    currentSortDirection: (asc ? 'asc' : 'desc') as SortDirection,
    onSort: handleSort,
  };

  return (
    <Table.Root variant="surface" size="2">
      <caption>
        <VisuallyHidden>{STRINGS.list.tableCaption}</VisuallyHidden>
      </caption>
      <Table.Header>
        <Table.Row>
          {SORTABLE_COLUMNS.map(({ sortKey, label }) => (
            <SortableTableHeader key={sortKey} sortKey={sortKey} {...sortProps}>
              {label}
            </SortableTableHeader>
          ))}
          <Table.ColumnHeaderCell>{STRINGS.columns.quarantine}</Table.ColumnHeaderCell>
          <SortableTableHeader sortKey="LAST_EVALUATION_TIME" {...sortProps}>
            {STRINGS.columns.lastEvaluation}
          </SortableTableHeader>
          {COUNT_COLUMNS.map(({ sortKey, label }) => (
            <SortableTableHeader key={sortKey} sortKey={sortKey} {...sortProps}>
              {label}
            </SortableTableHeader>
          ))}
          <Table.ColumnHeaderCell>{STRINGS.columns.quarantined}</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row, index) => (
          <Table.Row key={row.repositoryId || `${row.name}-${index}`}>
            <Table.Cell>
              <Flex align="center" gap="2">
                <Text>{row.name}</Text>
                {row.reevaluationRunning && (
                  <Badge color="blue" variant="soft" size="1">
                    {STRINGS.values.inProgress}
                  </Badge>
                )}
              </Flex>
            </Table.Cell>
            <Table.Cell>{row.format}</Table.Cell>
            <Table.Cell>{row.quarantineEnabled ? STRINGS.values.yes : STRINGS.values.no}</Table.Cell>
            <Table.Cell>{formatEvaluationTime(row.lastEvaluationTime)}</Table.Cell>
            <Table.Cell>{row.componentsEvaluated.toLocaleString()}</Table.Cell>
            <Table.Cell>
              <ThreatCountBadge count={row.criticalViolationCount} color="red" label={STRINGS.columns.critical} />
            </Table.Cell>
            <Table.Cell>
              <ThreatCountBadge count={row.severeViolationCount} color="orange" label={STRINGS.columns.severe} />
            </Table.Cell>
            <Table.Cell>
              <ThreatCountBadge count={row.moderateViolationCount} color="yellow" label={STRINGS.columns.moderate} />
            </Table.Cell>
            <Table.Cell>{row.quarantinedComponentCount.toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
